import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { Icon } from '~/components';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import { getWarehouseStatus } from '../api/utility';
import {
  ErrorDisplay,
  MaintenanceDisplay,
  NoAccess,
} from '../containers/LabelPerf/components';
import { useAmplifyUser } from '../context/useAmplifyUser';

export function CatalogPageLayout() {
  const { t } = useTranslation('label-perf');
  const { user, error: authError, isLoading: isAuthLoading } = useAmplifyUser();

  const [isInMaintenanceModeEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgLabelPerfMaintenanceEnabled,
  ]);

  const isInMaintenanceMode = useMemo(() => {
    const localStorageMaintenance =
      localStorage.getItem(FEATURE_FLAGS.WmgLabelPerfMaintenanceEnabled) ===
      'true';

    return localStorageMaintenance || isInMaintenanceModeEnabled;
  }, [isInMaintenanceModeEnabled]);

  const isUserAuthorized = useMemo(() => {
    if (isAuthLoading || !user) {
      return false;
    }

    if (!user.is_active) {
      return false;
    }

    const { role, major_labels } = user;

    if (role === 'admin' || role === 'supervisor') {
      return true;
    }

    if (role === 'user' && major_labels && major_labels?.length > 0) {
      return true;
    }

    return false;
  }, [user, isAuthLoading]);

  const { isLoading: isWarehouseLoading, isError: isWarehouseError } = useQuery(
    {
      queryKey: ['warehouseStatus'],
      queryFn: getWarehouseStatus,
      enabled: isUserAuthorized && !isInMaintenanceMode,
      retry: 60,
      retryDelay: 1000,
      refetchOnWindowFocus: false,
    },
  );

  if (isInMaintenanceMode) {
    return (
      <MaintenanceDisplay
        pageTitle={t('title.catalog')}
        title={t('error.maintenanceTitle')}
        description={t('error.maintenanceDesc')}
        className="m-xl min-h-[36rem]"
      />
    );
  }

  if (isAuthLoading || isWarehouseLoading) {
    return (
      <p className="p-xl">
        <Icon type="spinner" variant="solid" size="sm" className="fa-spin" />
      </p>
    );
  }

  if (authError) {
    if (authError.message === 'User not found') {
      return (
        <ErrorDisplay
          className="m-xl min-h-[10em]"
          title={t('error.userNotFound')}
        />
      );
    }

    return (
      <ErrorDisplay
        title={t('error.failedLoading')}
        description={t('error.refresh')}
        className="m-xl min-h-[20em]"
      />
    );
  }

  if (!isUserAuthorized) {
    return <NoAccess />;
  }

  if (isWarehouseError) {
    return (
      <ErrorDisplay
        title={t('error.warehouseFailed')}
        description={t('error.warehouseRefresh')}
        className="m-xl min-h-[20em]"
      />
    );
  }

  return (
    <>
      <Outlet />
    </>
  );
}

CatalogPageLayout.displayName = 'CatalogPageLayout';
